/**
 * お気に入りリスト印刷の行高・1頁件数。
 * 用紙サイズは mm なので、行高も mm で揃えないとプレビュー縮尺でずれてはみ出す。
 *
 * 印刷ページは A4 landscape または A3 portrait（マップ＋リスト同居）。
 * 用紙 CSS は iOS Safari のはみ出し対策で ISO より 1mm 小さい (209mm / 419mm)。
 * @see print-type-[printType].vue paperConfig / main.scss
 */

/** メモ行数スライダーの下限 */
export const MEMO_LINES_MIN = 1;
/** メモ行数スライダーの上限 */
export const MEMO_LINES_MAX = 8;
/** メモ行数の既定値 */
export const MEMO_LINES_DEFAULT = 2;

/** memoLines を有効範囲に収める */
export const clampMemoLines = (memoLines: number | undefined | null): number => {
  if (memoLines === undefined || memoLines === null || Number.isNaN(memoLines)) {
    return MEMO_LINES_DEFAULT;
  }
  return Math.min(MEMO_LINES_MAX, Math.max(MEMO_LINES_MIN, memoLines));
};

/** A4 横向きの用紙高さ (mm) — --papersize-A4LandscapeHeight */
const A4_LANDSCAPE_HEIGHT_MM = 209;
/** A3 縦向きの用紙高さ (mm) — --papersize-A3Height */
const A3_HEIGHT_MM = 419;

/** sheet の padding-top (mm) */
const SHEET_PADDING_TOP_MM = 10;
/** ComicMarket ヘッダーのおおよその高さ (mm) */
const PAGE_HEADER_MM = 10;
/** 下端の安全マージン (mm) */
const BOTTOM_MARGIN_MM = 5;
/** list-only-sheet の追加 padding-top を最悪ケースに含める (mm) */
const LIST_ONLY_EXTRA_MM = 8;

/** A3 マップ領域高さ (mm) — PrintMapCircle の height */
const A3_MAP_HEIGHT_MM = 180;
/** A3 リスト pane 上マージン (mm) — PrintSection7A3Sheet .a3-list-pane */
const A3_LIST_PANE_MARGIN_TOP_MM = 10;

/**
 * リスト本体（thead 含む）に使える高さ (mm)
 * - a4-landscape: リスト専用シート（マップは別シート）
 * - a3: マップと同シートの下半リスト
 */
export type PrintListPaper = "a4-landscape" | "a3";

export const getListHeightMm = (paper: PrintListPaper): number => {
  if (paper === "a3") {
    // pad + map header + map + list margin + list header + bottom
    return A3_HEIGHT_MM
      - SHEET_PADDING_TOP_MM
      - PAGE_HEADER_MM
      - A3_MAP_HEIGHT_MM
      - A3_LIST_PANE_MARGIN_TOP_MM
      - PAGE_HEADER_MM
      - BOTTOM_MARGIN_MM;
  }
  return A4_LANDSCAPE_HEIGHT_MM
    - SHEET_PADDING_TOP_MM
    - PAGE_HEADER_MM
    - BOTTOM_MARGIN_MM
    - LIST_ONLY_EXTRA_MM;
};

/** メモ1行あたりの行高 (mm)。CSS の --row-height と一致させる */
export const LINE_HEIGHT_MM = 5.5;

/** 見出し行の高さ (mm) */
export const THEAD_HEIGHT_MM = 6;

/**
 * memoLines と用紙種別から1頁あたりのサークル数を算出する。
 */
export const getCircleItemPerPage = (
  memoLines: number,
  paper: PrintListPaper = "a4-landscape",
): number => {
  const lines = clampMemoLines(memoLines);
  const listHeightMm = getListHeightMm(paper);
  return Math.max(1, Math.floor((listHeightMm - THEAD_HEIGHT_MM) / (LINE_HEIGHT_MM * lines)));
};

/** body 行の高さ (mm) */
export const getRowHeightMm = (memoLines: number): number =>
  LINE_HEIGHT_MM * clampMemoLines(memoLines);
